/**
 * src/api/voice.ts
 * -----------------
 * API functions for Helix voice features (STT and TTS).
 *
 * Fully implemented this will:
 *   - transcribeAudio(blob) — POST /voice/stt — sends a WebM/OGG audio
 *     blob to the backend STT endpoint (Whisper) and returns the transcript.
 *   - synthesizeSpeech(text, voiceId?) — POST /voice/tts — sends text to
 *     the backend TTS endpoint (ElevenLabs or OpenAI) and returns an
 *     ArrayBuffer of MP3/OGG audio for playback via the audio library.
 *   - getVoiceConfig() — GET /voice/config — returns available voice IDs
 *     and the org's configured default voice.
 *
 * In Voice-to-Voice mode the audio pipeline runs over WebSocket
 * (see src/lib/wsClient.ts) for lower latency; these REST functions
 * are used only for Voice-to-Text transcription mode.
 *
 * Key dependencies:
 *   src/api/client.ts, src/types/voice.ts, src/lib/audio.ts
 */
import apiClient from './client'
import i18n from '@/lib/i18n'
import { getCurrentOrgId } from '@/lib/org'
import type { VoiceConfig } from '@/types/voice'

/**
 * Transcribe a recorded audio blob to text via the backend STT service.
 *
 * Sends the locale the customer is using alongside the audio. Transcribing with
 * no language hint leaves the model to auto-detect from the audio alone, and on
 * a short or noisy clip that guess is unreliable — English speech came back as
 * Arabic text, and the reply and spoken voice inherited the mistake.
 */
export async function transcribeAudio(audioBlob: Blob): Promise<string> {
  const formData = new FormData()
  formData.append('audio', audioBlob, 'recording.webm')
  formData.append('language', i18n.language || 'en')
  formData.append('org_id', getCurrentOrgId())
  const { data } = await apiClient.post<{ transcript: string }>(
    '/voice/stt',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data.transcript
}

/** Synthesize text to speech and return raw audio bytes. */
export async function synthesizeSpeech(
  text: string,
  voiceId?: string,
): Promise<ArrayBuffer> {
  const { data } = await apiClient.post(
    '/voice/tts',
    { text, voice_id: voiceId },
    { responseType: 'arraybuffer' },
  )
  return data as ArrayBuffer
}

/** Retrieve org voice configuration (available voices, default voice ID). */
export async function getVoiceConfig(): Promise<VoiceConfig> {
  const { data } = await apiClient.get<VoiceConfig>('/voice/config')
  return data
}
